import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Lead } from '../types/leads';
import { useChatbot } from '../contexts/ChatbotContext';
import { processUnsentLeads } from '../utils/webhookHandler';
import { useSearchParams } from 'react-router-dom';

// Étendre l'interface Lead pour ajouter la propriété highlighted
interface ExtendedLead extends Lead {
  highlighted?: boolean;
}

export function useLeads() {
  const [leads, setLeads] = useState<ExtendedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { chatbotPublicId } = useChatbot();
  const [searchParams] = useSearchParams();
  
  // Get chatbot ID from URL or use the selected one
  const urlChatbotId = searchParams.get('chatbot');
  const urlLeadId = searchParams.get('lead');
  const urlConversationId = searchParams.get('conversation');
  
  // Use URL parameter if available, otherwise use the current chatbot
  const activeChatbotId = urlChatbotId || chatbotPublicId;

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!activeChatbotId) {
        throw new Error('No chatbot selected');
      }

      console.log(`Fetching leads for chatbot: ${activeChatbotId}, highlight lead: ${urlLeadId}`);
      
      // Toujours récupérer tous les leads du chatbot
      const { data, error } = await supabase
        .from('conversation_contact_info')
        .select(`
          *,
          conversations!inner(
            id,
            initial_message_content,
            created_at
          )
        `)
        .eq('chatbot_public_id', activeChatbotId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Formatage et marquage des leads à mettre en évidence
      const formattedLeads = data.map(lead => {
        const isHighlighted = urlLeadId === lead.id || 
                            (urlConversationId && urlConversationId === lead.conversation_id);
        
        return {
          id: lead.id,
          type: lead.type as 'email' | 'phone',
          value: lead.value,
          conversationId: lead.conversation_id,
          messageId: lead.message_id,
          firstMessage: lead.conversations.initial_message_content || '',
          createdAt: lead.created_at,
          processed: lead.processed || false,
          highlighted: isHighlighted
        };
      });

      setLeads(formattedLeads);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  };

  const toggleLeadStatus = async (id: string) => {
    try {
      if (!activeChatbotId) {
        throw new Error('No chatbot selected');
      }

      const lead = leads.find(l => l.id === id);
      if (!lead) return;

      const { error } = await supabase
        .from('conversation_contact_info')
        .update({ processed: !lead.processed })
        .eq('id', id)
        .eq('chatbot_public_id', activeChatbotId);

      if (error) throw error;

      setLeads(prevLeads => 
        prevLeads.map(l => 
          l.id === id ? { ...l, processed: !l.processed } : l
        )
      );
    } catch (err) {
      console.error('Error toggling lead status:', err);
      throw err;
    }
  };

  const deleteLeads = async (leadIds: string[]) => {
    try {
      if (!activeChatbotId) {
        throw new Error('No chatbot selected');
      }

      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('conversation_contact_info')
        .delete()
        .in('id', leadIds)
        .eq('chatbot_public_id', activeChatbotId);

      if (error) throw error;

      setLeads(prevLeads => prevLeads.filter(lead => !leadIds.includes(lead.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression des leads');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const processLeadsForWebhooks = async () => {
    if (!activeChatbotId) return;
    
    try {
      await processUnsentLeads(activeChatbotId);
    } catch (error) {
      console.error('Error processing leads for webhooks:', error);
    }
  };

  useEffect(() => {
    if (!activeChatbotId) return;

    fetchLeads();
    
    // Process any existing unprocessed leads
    processLeadsForWebhooks();
    
    // Set up subscription for new leads
    const subscription = supabase
      .channel('conversation_contact_info_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_contact_info',
          filter: `chatbot_public_id=eq.${activeChatbotId}`
        },
        (payload) => {
          console.log('New lead detected in subscription:', payload);
          fetchLeads();
          
          // Also process the lead for webhooks when a new lead is inserted
          processLeadsForWebhooks();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [activeChatbotId, urlLeadId, urlConversationId]);

  return { 
    leads, 
    loading, 
    error, 
    toggleLeadStatus, 
    deleteLeads,
    highlightedLeadId: urlLeadId,
    highlightedConversationId: urlConversationId
  };
}